// Shared back-link utility for screens that render a "Back to X" affordance
// driven by navigation state. Three historical sender shapes are normalized
// into one canonical { fromLabel, fromPath } pair; the path-to-label lookup
// (exact + prefix) is maintained in this single file so labels don't drift
// between screens. Screens supply their own fallback — there is no global
// default, because where "back" should go depends on where users typically
// deep-link from, and that's screen-specific.

export interface BackLinkState {
  fromLabel?: string;
  fromPath?: string;
}

export interface BackLinkFallback {
  label: string;
  path: string;
}

export interface ResolvedBackLink {
  label: string;
  path: string;
  /** True when no sender state matched and the fallback was used. Useful for
   *  telemetry or for screens that want to hide the link entirely in that case. */
  isFallback: boolean;
}

/**
 * Canonical navigation-state builder. Senders call this to produce the
 * { fromLabel, fromPath } shape receivers expect. Keep every new call site
 * on this helper; the legacy shapes below are accepted for compatibility
 * with existing senders but new code should not emit them.
 *
 * `label` is optional. When omitted, the receiver derives a label from the
 * path via the shared lookup table, falling back to the receiver's default.
 * Prefer supplying an explicit label - senders know what they're called and
 * inference is a convenience for generic list-to-detail links where the
 * caller is the same template rendering many pathnames.
 */
export function buildBackLinkState(labelOrPath: string, path?: string): BackLinkState {
  if (path === undefined) {
    return { fromPath: labelOrPath };
  }
  return { fromLabel: labelOrPath, fromPath: path };
}

// Known-path -> back-label map. Exact-match keys first; prefix fallbacks below.
// Keep in sync with apps/chat/src/router/microsite.router.tsx so labels
// don't drift from the actual routes. If a route doesn't appear here, the
// sender should supply `fromLabel` explicitly via buildBackLinkState.
const BACK_LINK_LABELS: Record<string, string> = {
  '/': 'Back to Home',
  '/home': 'Back to Home',
  '/main': 'Back to Home',
  '/segmentation': 'Back to Segmentation',
  '/explore-segments': 'Back to Explore Segments',
  '/message-library': 'Back to Message Library',
  '/message-generator': 'Back to Message Generator',
  '/chat': 'Back to Chat',
};

// Prefix fallbacks for dynamic routes. Checked after exact-match misses.
// Ordered - first match wins, so put more specific prefixes above broader ones.
const BACK_LINK_PREFIX_LABELS: Array<{ prefix: string; label: string }> = [
  { prefix: '/segmentation/segment/', label: 'Back to Segment Profile' },
  { prefix: '/segmentation/cross-segment-dashboard', label: 'Back to Cross-Segment Dashboard' },
  { prefix: '/message-generator/', label: 'Back to Message Generator' },
  { prefix: '/message-testing/', label: 'Back to Message Testing' },
  { prefix: '/projects/', label: 'Back to Project' },
  { prefix: '/workspaces', label: 'Back to Workspace' },
  { prefix: '/pages/', label: 'Back to Page' },
  { prefix: '/chat/', label: 'Back to Chat' },
];

/**
 * Derive a human label from a path. Tries exact match, then exact-match on
 * the bare path (querystring stripped), then prefix match. Returns null if
 * nothing matches - callers decide the fallback.
 */
export function labelForPath(path: string): string | null {
  if (BACK_LINK_LABELS[path]) return BACK_LINK_LABELS[path];
  const bare = path.split('?')[0];
  if (BACK_LINK_LABELS[bare]) return BACK_LINK_LABELS[bare];
  for (const { prefix, label } of BACK_LINK_PREFIX_LABELS) {
    if (bare.startsWith(prefix)) return label;
  }
  return null;
}

// --- Legacy shape normalization ---------------------------------------------
//
// Three historical sender shapes exist in the codebase:
//   A. { from: string }                             - path only, label inferred
//   B. { from: { path: string, label?: string } }   - object with optional label
//   C. { fromLabel: string, fromPath: string }      - canonical (this module)
//
// This function accepts any of the three and returns a normalized pair.
// New senders should use buildBackLinkState(). Legacy branches are kept
// until all senders are migrated.

interface LegacyObjectShape {
  from?: string | { path?: string; label?: string } | null;
  fromLabel?: string;
  fromPath?: string;
}

function normalizeState(state: unknown): BackLinkState {
  if (!state || typeof state !== 'object') return {};
  const s = state as LegacyObjectShape;

  // Canonical C - already normalized.
  if (typeof s.fromPath === 'string') {
    return { fromLabel: s.fromLabel, fromPath: s.fromPath };
  }

  const from = s.from;
  if (from == null) return {};

  // Legacy B - object.
  if (typeof from === 'object') {
    const path = typeof from.path === 'string' ? from.path : undefined;
    const label = typeof from.label === 'string' ? from.label : undefined;
    return { fromLabel: label, fromPath: path };
  }

  // Legacy A - bare string path.
  if (typeof from === 'string') {
    return { fromPath: from };
  }

  return {};
}

/**
 * Resolve a back-link label + path from navigation state. Screens supply
 * their own `fallback` so deep-link arrivals (no state) land somewhere
 * plausible for that screen. The returned `path` is always a concrete
 * route string - screens pass it to `navigate(path)` for deterministic
 * return behavior (works with new-tab, copy-paste, and history).
 *
 * If you need browser-history "go back" behavior (navigate(-1)), make
 * that decision at the call site. This utility deliberately doesn't
 * return -1 because it makes deep-links unrecoverable.
 */
export function resolveBackLink(state: unknown, fallback: BackLinkFallback): ResolvedBackLink {
  const { fromLabel, fromPath } = normalizeState(state);

  if (fromPath) {
    return {
      path: fromPath,
      label: fromLabel ?? labelForPath(fromPath) ?? fallback.label,
      isFallback: false,
    };
  }

  return {
    path: fallback.path,
    label: fallback.label,
    isFallback: true,
  };
}
